<template>
  <Modal
    v-if="visible"
    :id="id"
    :visible="visible"
    :title="title"
    size="half"
    :button1-caption="nextButton.caption"
    :button1-class="nextButton.class"
    :button1-visible="nextButton.visible"
    :button1-disabled="nextButton.disabled"
    :button1-callback="() => onNextClicked()"
    :button2-caption="previousButton.caption"
    :button2-class="previousButton.class"
    :button2-visible="previousButton.visible"
    :button2-disabled="previousButton.disabled"
    :button2-callback="() => stepper.goPrevious()"
    @cancelled="() => cancel()">
    <template
      v-if="icon"
      #modal-header-icon>
      <component :is="icon" />
    </template>
    <template #modal-after-form>
      <Stepper
        ref="stepper"
        :steps="steps">
        <template
          v-for="(_, slot) in $slots"
          #[slot]="data">
          <slot
            :name="slot"
            v-bind="data" />
        </template>
      </Stepper>
    </template>
  </Modal>
</template>

<script setup lang="ts">
  import { type Component, computed, ref, useTemplateRef } from 'vue';
  import { ComponentExposed } from 'vue-component-type-helpers';

  import Modal from '@/common/components/controls/Modal.vue';
  import Stepper, { IStep } from '@/common/components/templates/Stepper.vue';

  export interface IModalStep extends IStep {
    previousButtonCaption?: string;
    nextButtonCaption?: string;
  }

  interface IProps {
    title: string;
    steps: IModalStep[];
    id: string;
    icon?: Component;
  }

  const { steps, title } = defineProps<IProps>();

  const emits = defineEmits(['cancelled', 'completed', 'stepChanged']);

  const visible = ref(false);
  const stepper = useTemplateRef<ComponentExposed<typeof Stepper>>('stepper');

  interface IModalButton {
    caption: string;
    class: string;
    visible: boolean;
    disabled: boolean;
  }

  const currentStep = computed(() => stepper.value?.currentStep as IModalStep);

  const isFirstStep = computed(() => {
    return currentStep.value?.id === steps[0].id;
  });

  const isLastStep = computed(() => {
    return currentStep.value?.id === steps[steps.length - 1].id;
  });

  const nextButton = computed<IModalButton>(() => {
    const defaultCaption = isLastStep.value ? 'Complete' : 'Next';
    const caption = currentStep.value?.nextButtonCaption || defaultCaption;
    const disabled = currentStep.value?.isCompleted.value === false;
    return {
      caption,
      class: 'primary',
      visible: true,
      disabled,
    };
  });

  const previousButton = computed<IModalButton>(() => {
    const caption = currentStep.value?.previousButtonCaption || 'Previous';
    const visible = !isFirstStep.value && !isLastStep.value;
    return {
      caption,
      class: null,
      visible: visible,
      disabled: false,
    };
  });

  function goToStep(stepId: string) {
    stepper.value?.goToStep(stepId);
  }

  function onNextClicked() {
    emits('stepChanged', stepper.value?.currentStep?.id);
    if (isLastStep.value) {
      emits('completed');
      return;
    }
    stepper.value?.goNext();
  }

  function open() {
    visible.value = true;
  }

  function close() {
    visible.value = false;
  }

  function cancel() {
    close();
    emits('cancelled');
  }

  defineExpose({
    open,
    close,
    goToStep,
  });
</script>
